import { handleCheckoutStart, handleTrialStart } from '@/lib/gtag'
import { plans } from '@/utils/data/plans'
import { trpc } from '@/utils/trpc'
import * as Sentry from '@sentry/react'
import { createContext, useContext, useEffect, useReducer } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import useMonitorSubscription from './useMonitorSubscription'
import showToastNotification from './useShowToast'

type CheckoutSubscriptionMutationFn = typeof trpc.checkoutSubscription.useMutation
type CheckoutInput = Parameters<ReturnType<CheckoutSubscriptionMutationFn>['mutate']>[0]

type CheckoutState = {
  isSubscribed: boolean
  isSubscribing: boolean
}

type InitializeCheckoutStateAction = {
  type: 'INITIALIZE_CHECKOUT_STATE'
  payload: CheckoutState
}

type CheckoutAction =
  | {
      type: 'CHANGE_SUBSCRIPTION_STATUS'
      payload: {
        isSubscribed: boolean
        isSubscribing: boolean
      }
    }
  | InitializeCheckoutStateAction
  | {
      type: 'START_CHECKOUT'
    }
  | {
      type: 'END_CHECKOUT'
    }

const initialState: CheckoutState = {
  isSubscribed: false,
  isSubscribing: false,
}

type CheckoutValues = {
  isSubscribed: boolean
  isSubscribing: boolean
  checkoutSubscription: (params: CheckoutInput) => Promise<unknown>
}

const CheckoutContext = createContext<CheckoutValues | undefined>(undefined)

const checkoutReducer = (state: CheckoutState, action: CheckoutAction) => {
  switch (action.type) {
    case 'CHANGE_SUBSCRIPTION_STATUS':
      state = {
        ...state,
        isSubscribed: action.payload.isSubscribed,
        isSubscribing: action.payload.isSubscribing,
      }
      return state
    case 'INITIALIZE_CHECKOUT_STATE':
      state = {
        ...state,
        isSubscribed: action.payload.isSubscribed,
        isSubscribing: action.payload.isSubscribing,
      }
      return state
    case 'START_CHECKOUT':
      state = {
        ...state,
        isSubscribing: true,
      }
      return state
    case 'END_CHECKOUT':
      state = {
        ...state,
        isSubscribing: false,
      }
      return state
    default:
      return state
  }
}

export const useCheckout = () => {
  const context = useContext(CheckoutContext)
  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider')
  }
  return context
}

export const CheckoutProvider = ({ children }: { children: React.PropsWithChildren['children'] }) => {
  const [state, dispatch] = useReducer(checkoutReducer, initialState)
  const [, , removeCoupon] = useLocalStorage<string | undefined>('cos_coupon', undefined)

  const { mutateAsync: checkoutSubscriptionAsync, isSuccess: isSubscriptionSuccessful } =
    trpc.checkoutSubscription.useMutation({
      trpc: { context: { idempotent: true } },
    })

  const { subscriptionSuccessful: isSubscribed } = useMonitorSubscription(isSubscriptionSuccessful)

  useEffect(() => {
    if (!isSubscribed) {
      return
    }
    dispatch({
      type: 'CHANGE_SUBSCRIPTION_STATUS',
      payload: {
        isSubscribed,
        isSubscribing: false,
      },
    })
  }, [isSubscribed])

  const checkoutSubscription = async (...params: Parameters<typeof checkoutSubscriptionAsync>) => {
    try {
      dispatch({
        type: 'START_CHECKOUT',
      })
      const selectedPlanId = params[0].plans[0]
      const planDetail = plans.find((plan) => plan.plan_id === selectedPlanId)
      if (selectedPlanId && planDetail) {
        handleCheckoutStart([
          {
            item_id: params[0].plans[0],
            item_name: planDetail.name,
            price: parseInt(planDetail.price.replace('$', ''), 10),
            quantity: 1,
          },
        ])
      }
      await checkoutSubscriptionAsync(...params)
      removeCoupon()
      if (selectedPlanId && planDetail) {
        handleTrialStart(`${new Date()}`, parseInt(planDetail?.price.replace('$', ''), 10), [
          {
            item_id: planDetail?.plan_id,
            item_name: planDetail?.name,
            quantity: 1,
          },
        ])
      }
    } catch (e) {
      dispatch({
        type: 'CHANGE_SUBSCRIPTION_STATUS',
        payload: {
          isSubscribed: false,
          isSubscribing: false,
        },
      })

      showToastNotification('error', {
        message: 'Could not create your subscription',
      })
      Sentry.captureException(e)
    }
  }

  return (
    <CheckoutContext.Provider
      value={{
        isSubscribed: state.isSubscribed,
        isSubscribing: state.isSubscribing,
        checkoutSubscription,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  )
}
